import {
  IntegrationStatusTypes,
  SalesforceConfig,
  SalesforceIntegrationId,
} from 'module/api/integrations/salesforce/types';
import { useTranslation } from 'module/hooks/use-translation';
import {
  InlineNotification,
  InlineNotificationType,
} from 'module/ui/components/atoms/inline-notification';
import { Modal } from 'module/ui/components/atoms/modal';
import {
  faArrowRight,
  faInfo,
  faLink,
  faUnlink,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useParams } from '@rocketlane/rocket-router';
import { Button } from '@rocketlane/ui';
import { Tooltip, Typography } from 'antd';
import {
  useDisconnectSalesForceMutation,
  useGetSfInstallationsQuery,
  useSalesForceAuthenticateQuery,
} from 'queries/salesforce';
import { ReactElement, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SalesforceTabs } from 'components/molecules/integrations/salesforce/tabs';
import { SalesforceTabsType } from 'components/molecules/integrations/salesforce/tabs';
import { SalesforceLayout } from 'components/pages/integrations/salesforce/salesforce-layout';
import { SalesforceEmailSettings } from './email-settings';

// Styled components for alert
const AlertButton = styled(Button)`
  /* Add any specific styling for alert button */
`;

const Message = styled.div`
  /* Add any specific styling for message */
`;

const { Text } = Typography;

const SubText = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #525252;
  margin-top: 8px;
`;

const CardContainer = styled.div`
  background: white;
  border: 1px solid #f2f2f2;
  border-radius: 8px;
  padding: 24px;
  width: 772px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Container = styled.div`
  padding: 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

const StyledSettingsTitle = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-size: 14px;
  font-weight: 590;
  line-height: 20px;
  color: #161616;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const InstalledSuccessfullyText = styled.p`
  font-family: 'SF Pro', sans-serif;
  font-size: 14px;
  font-weight: 590;
  line-height: 18px;
  color: #24a148;
  margin: 0;
`;

// const CheckMarkWrapper = styled.span<{ isHidden?: boolean }>`
//   margin-right: 8px;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   width: 16px;
//   height: 16px;
//   visibility: ${(props) => (props.isHidden ? 'hidden' : 'visible')};
//   svg {
//     color: #24a148;
//     font-size: 12px;
//   }
// `;

const StyledStrong = styled.strong`
  margin-right: var(--spacing-02);
`;

const StyledButton = styled(Button)`
  font-family: 'SF Pro', sans-serif;
  font-size: 14px;
  font-weight: 590;
  line-height: 18px;
  padding: 8px;
  border-radius: 8px;
  width: max-content;
  height: 32px;
  &.ant-btn {
    padding-bottom: 0;
  }
`;

const StyledButtonAlt = styled(Button)`
  font-size: 14px;
  font-weight: 590;
  line-height: 18px;
  padding: 8px;
  border-radius: 8px;
  height: 32px;
  width: max-content;
  background-color: #24a148;
  border-color: #24a148;
  color: #ffffff;

  &:hover {
    background-color: #1e8e3e;
    border-color: #1e8e3e;
  }
`;

const StyledText = styled(Text)`
  font-family: 'SF Pro', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #525252;
  margin: 0;
`;

const NotificationTitle = styled.p`
  font-size: 14px;
  font-weight: 590;
  line-height: 16px;
  color: #161616;
  margin: 0;
  width: 772px;
`;

const SalesforceSettingLayout = ({
  btnText,
  icon,
  subtext,
  href,
  disabled,
  loading,
  heading,
  isTextOnly,
}: {
  btnText: ReactElement;
  icon: ReactElement;
  subtext: ReactElement;
  href: string | undefined;
  disabled: boolean;
  loading: boolean;
  heading?: ReactElement;
  isTextOnly?: boolean;
  isStepCompleted?: boolean;
}) => (
  <CardContainer>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '88px' }}>
        <div style={{ flex: 1 }}>
          <StyledSettingsTitle>
            {/* <CheckMark isCompleted={isStepCompleted} /> */}
            {heading}
          </StyledSettingsTitle>
          <SubText>{subtext}</SubText>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {isTextOnly ? (
          btnText
        ) : (
          <StyledButton
            variant="primary"
            rightIcon={icon}
            onClick={() => {
              if (href && !disabled) {
                window.open(href, '_blank', 'noopener,noreferrer');
              }
            }}
            disabled={disabled}
            loading={loading}
          >
            {btnText}
          </StyledButton>
        )}
      </div>
    </div>
  </CardContainer>
);

export const SalesforceSettings = ({ data }: { data?: SalesforceConfig }) => {
  const { integrationId } = useParams<SalesforceIntegrationId>();
  const history = useNavigate();

  const { mutate, isPending } = useDisconnectSalesForceMutation();
  const { isLoading: isLoadingAuth, data: authDetails } =
    useSalesForceAuthenticateQuery({});

  const { data: installationData } = useGetSfInstallationsQuery(
    Number(integrationId),
    integrationId !== 'new',
  );
  const [showDisconnectButton, setShowDisconnectButton] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const onMouserHandle = (status = false) => {
    setShowDisconnectButton(status);
  };

  const handleDisconnect = () => {
    setShowDisconnectModal(true);
  };

  const onDisconnect = async () => {
    if (integrationId !== 'new') {
      await mutate(Number(integrationId), {
        onSuccess: () => {
          history('/settings/integrations/');
        },
      });
    }
  };
  const alertMessage = useTranslation('integration.alert.error.message');
  const reAuthMessage = useTranslation(
    'settings.integrations.sf.settings.alert.button.message',
  );
  const reAuthenticate = useTranslation(
    'settings.integration.alert.button.message',
  );
  // const isStepCompleted = integrationId === 'new';

  return (
    <SalesforceLayout
      tabs={
        <SalesforceTabs
          activeKey={SalesforceTabsType.Settings}
          integrationId={integrationId}
          integrationStatus={data?.integrationStatus}
        />
      }
      content={
        <Container>
          {integrationId !== 'new' ? (
            <>
              {(data?.integrationStatus === IntegrationStatusTypes.Expired ||
                data?.requiresReAuthentication) && (
                <InlineNotification
                  msg={
                    <Message>
                      <strong>{alertMessage}</strong> {reAuthMessage}
                    </Message>
                  }
                  type={InlineNotificationType.Error}
                  button={
                    <AlertButton
                      size="small"
                      variant="secondary"
                      onClick={() =>
                        window.open(authDetails?.oauthReauthUrl, '_blank')
                      }
                    >
                      {reAuthenticate}
                    </AlertButton>
                  }
                  icon={<FontAwesomeIcon icon={faInfo} />}
                />
              )}
              <CardContainer>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '88px',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <StyledSettingsTitle>
                        {/* <CheckMark isCompleted={isStepCompleted} /> */}
                        <FormattedMessage
                          id={
                            !authDetails?.marketplaceAppInstallationRequired
                              ? 'settings.integrations.sf.settings.title_without_step'
                              : 'settings.integrations.sf.settings.title'
                          }
                          values={{
                            strong: (chunks) => (
                              <StyledStrong>{chunks}</StyledStrong>
                            ),
                          }}
                        />
                      </StyledSettingsTitle>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <Tooltip
                      title={<FormattedMessage id="sf.connected.message" />}
                      open={showDisconnectButton}
                      placement="bottom"
                    >
                      <StyledButtonAlt
                        variant={showDisconnectButton ? 'danger' : 'primary'}
                        rightIcon={
                          showDisconnectButton ? (
                            <FontAwesomeIcon icon={faUnlink} />
                          ) : (
                            <FontAwesomeIcon icon={faLink} />
                          )
                        }
                        onClick={handleDisconnect}
                        onMouseEnter={() => onMouserHandle(true)}
                        onMouseLeave={() => onMouserHandle(false)}
                      >
                        {data?.meta.salesforceEnv === 'SANDBOX' ? (
                          showDisconnectButton ? (
                            <FormattedMessage id="disconnect" />
                          ) : (
                            <FormattedMessage id="settings.integrations.sf.settings.connected.sandbox" />
                          )
                        ) : showDisconnectButton ? (
                          <FormattedMessage id="disconnect" />
                        ) : (
                          <FormattedMessage id="settings.integrations.sf.settings.connected.production" />
                        )}
                      </StyledButtonAlt>
                    </Tooltip>
                    {data?.meta.oauthAuthenticatorEmail ? (
                      <StyledText>
                        {data?.meta.oauthAuthenticatorAccountUrl ? (
                          <FormattedMessage
                            id="settings.integrations.sf.settings.connected.to.as"
                            values={{
                              connector: (
                                <b>
                                  {data?.meta.oauthAuthenticatorUsername ||
                                    data?.meta.oauthAuthenticatorEmail}
                                </b>
                              ),
                              company: (
                                <b>
                                  {
                                    data?.meta.oauthAuthenticatorAccountUrl?.split(
                                      'https://',
                                    )[1]
                                  }
                                </b>
                              ),
                            }}
                          />
                        ) : (
                          <FormattedMessage
                            id="settings.integrations.sf.settings.connected.as"
                            values={{
                              connector: (
                                <b>{data?.meta.oauthAuthenticatorEmail}</b>
                              ),
                            }}
                          />
                        )}
                      </StyledText>
                    ) : null}
                  </div>
                </div>
              </CardContainer>

              {authDetails?.marketplaceAppInstallationRequired === true ? (
                installationData !== undefined &&
                installationData?.length > 0 ? (
                  <SalesforceSettingLayout
                    icon={<FontAwesomeIcon icon={faArrowRight} />}
                    href={authDetails?.marketplaceAppUrl}
                    disabled={integrationId === 'new'}
                    heading={
                      <FormattedMessage
                        id="settings.integrations.sf.settings.install_app_title"
                        values={{
                          strong: (chunks) => (
                            <StyledStrong>{chunks}</StyledStrong>
                          ),
                        }}
                      />
                    }
                    btnText={
                      <InstalledSuccessfullyText>
                        <FormattedMessage id="sf.app.installed_successfully" />
                      </InstalledSuccessfullyText>
                    }
                    subtext={<></>}
                    loading={isLoadingAuth}
                    isTextOnly
                    isStepCompleted
                  />
                ) : (
                  <SalesforceSettingLayout
                    icon={<FontAwesomeIcon icon={faArrowRight} />}
                    href={authDetails?.marketplaceAppUrl}
                    disabled={integrationId === 'new'}
                    heading={
                      <FormattedMessage
                        id="settings.integrations.sf.settings.install_app_title"
                        values={{
                          strong: (chunks) => (
                            <StyledStrong>{chunks}</StyledStrong>
                          ),
                        }}
                      />
                    }
                    btnText={
                      <FormattedMessage id="settings.integrations.sf.settings.install_rocketlane_in_sf" />
                    }
                    subtext={<></>}
                    loading={isLoadingAuth}
                  />
                )
              ) : null}

              <NotificationTitle>Notification</NotificationTitle>
              <CardContainer>
                <SalesforceEmailSettings
                  integrationId={
                    integrationId ? Number(integrationId) : undefined
                  }
                  config={data}
                />
              </CardContainer>

              <Modal
                open={showDisconnectModal}
                title={
                  <FormattedMessage id="settings.integrations.sf.disconnect.title" />
                }
                confirmLoading={false}
                onCancel={() => setShowDisconnectModal(false)}
                onOk={onDisconnect}
                okText={<FormattedMessage id="disconnect" />}
                okType="danger"
                okButtonProps={{ loading: isPending }}
                cancelButtonProps={{ disabled: isPending }}
                closable={!isPending}
              >
                <p>
                  <FormattedMessage id="settings.integrations.sf.disconnect.content" />
                </p>
              </Modal>
            </>
          ) : (
            <>
              <SalesforceSettingLayout
                heading={
                  <FormattedMessage
                    id={
                      !authDetails?.marketplaceAppInstallationRequired
                        ? 'settings.integrations.sf.settings.title_without_step'
                        : 'settings.integrations.sf.settings.title'
                    }
                    values={{
                      strong: (chunks) => <StyledStrong>{chunks}</StyledStrong>,
                    }}
                  />
                }
                icon={<FontAwesomeIcon icon={faArrowRight} />}
                href={authDetails?.oauthUrl}
                disabled={false}
                btnText={
                  <FormattedMessage id="settings.integrations.sf.settings.connect.production" />
                }
                subtext={
                  <FormattedMessage
                    id="settings.integrations.sf.settings.connect.production.subtext"
                    values={{
                      a: (chunks) => (
                        <a href={authDetails?.oauthSandboxUrl}>{chunks}</a>
                      ),
                    }}
                  />
                }
                loading={isLoadingAuth}
              />
              {authDetails?.marketplaceAppInstallationRequired === true ? (
                <SalesforceSettingLayout
                  icon={<FontAwesomeIcon icon={faArrowRight} />}
                  href={authDetails?.marketplaceAppUrl}
                  disabled={integrationId === 'new'}
                  heading={
                    <FormattedMessage
                      id="settings.integrations.sf.settings.install_app_title"
                      values={{
                        strong: (chunks) => (
                          <StyledStrong>{chunks}</StyledStrong>
                        ),
                      }}
                    />
                  }
                  btnText={
                    <FormattedMessage id="settings.integrations.sf.settings.install_rocketlane_in_sf" />
                  }
                  subtext={<></>}
                  loading={isLoadingAuth}
                />
              ) : null}
            </>
          )}
        </Container>
      }
      actions={{}}
    />
  );
};
